import { type FileParent, fileWriteIsDeniedFor } from "@msuite/citadel";
import type { Tables } from "@msuite/utils/types";
import { useCitadel } from "./use-citadel";

/**
 * What the caller may do with the attachments of one surface, asked of the parent that surface
 * stands on.
 *
 * A file carries no rights of its own. Reading asks no rule — every member reads the organisation's
 * files — so nothing here answers it, and a file query is enabled as any other ungated read is.
 * Writing follows the parent: attaching, unlinking, replacing, moving, annotating and removing are
 * all `update` on the record the file hangs on, the ticket rule for a ticket, and uploader or
 * administrator for a file that hangs on nothing. That is the question the server asks before it
 * writes (`Files.authorizeParent`, `Files.authorizeFile`), and the two must agree, because a
 * control that is shown and answered 403 is worse than one that is hidden.
 *
 * **Only a definitive refusal hides a control.** While the rules load, and while the parent itself
 * is still loading (`undefined`), every answer is `true`: a paperclip that blinks out and back in
 * as the claims arrive reads as broken, and the server has the last word either way — the same
 * contract `isDenied` keeps.
 *
 * The server asks a file's removal of **every** parent it is linked to, and a surface knows only
 * the one it stands on. A file shared between this parent and another the caller may not change is
 * therefore offered and then refused; that case is rare enough to leave to the server's toast.
 *
 * @param parent - The parent the surface stands on — the worksite documents and the workspace
 *   media name `Organization` with `{ id: orgId, orgId }`; `null` when its files hang on nothing
 *   (the inbox), `undefined` while the parent has not loaded yet.
 * @returns `canWrite` for the surface's own controls — attach, pick, upload — and `canWriteFile`
 *   for one file's controls, which on a parent-less surface asks the uploader-or-administrator rule
 *   of that file.
 */
export function useFileCapabilities(parent: FileParent | null | undefined) {
	/** Hooks */
	const { citadel } = useCitadel();

	/** Computed */
	const canWrite = !parent || !fileWriteIsDeniedFor(citadel, parent);

	/** Functions */
	function canWriteFile(file: Pick<Tables<"files">, "createdBy">) {
		if (parent === null) return !fileWriteIsDeniedFor(citadel, { file });
		return canWrite;
	}

	/** Return */
	return { canWrite, canWriteFile };
}
